import { MutationOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useContext } from 'react';

import AppContext from '@/contexts/appContext';
import WorkRepository from '@/repositories/WorkRepository';
import type { Item, ItemPayload } from '@/types/item';
import type RepositoryResponse from '@/types/repositoryResponse';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';

type UpdateItemVariables = { id: string; values: Partial<ItemPayload> };

export const useUpdateItem = (
  options?: MutationOptions<RepositoryResponse<Item>, AxiosError, UpdateItemVariables>
) => {
  const workRepository = WorkRepository();
  const queryClient = useQueryClient();
  const { addMessage } = useContext(AppContext);

  return useMutation({
    ...options,
    mutationFn: ({ id, values }: UpdateItemVariables) => workRepository.updateItem(id, values),
    onSuccess: (response, ...rest) => {
      const updated = response.data;

      if (updated) {
        queryClient.invalidateQueries({ queryKey: ['getPersonTasks', updated.person_id] });
        queryClient.invalidateQueries({ queryKey: ['getTasks', updated.taskable_id] });
        queryClient.invalidateQueries({ queryKey: ['getAllTasks'] });
      }

      options?.onSuccess?.(response, ...rest);
    },
    onError: (error, ...rest) => {
      addMessage(mapAxiosErrorMessages(error));
      options?.onError?.(error, ...rest);
    }
  });
};

export default useUpdateItem;
